import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, forkJoin, defer, of, Subscription } from 'rxjs';
import { NotificationService } from '../../../services/notification.service';
import { ChecklistsService, ModulesDropdownOptions } from '../../../services/checklist.service';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { get, includes, isEqual, isNil, last, toNumber } from 'lodash-es';
import {
  AnswerableChecklistPrompt,
  AvailablePeriodOption,
  Checklist,
  ChecklistAssetType,
  ChecklistPrompt,
  ChecklistPromptGroup,
  ChecklistPromptGroupConfiguration,
  ChecklistRequiredBefore,
  ChecklistType,
  PromptFieldEntry
} from '../../../module/checklists/shared/types';
import { TranslateService } from '@ngx-translate/core';
import { PusherService } from '../../../services/pusher.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { FieldmagicDropdownOption } from '../../../shared/components/forms/input/dropdown/dropdown.component';
import {
  filled,
  blank,
  generateId,
  data_get,
  observe,
  filter_array,
  spf,
  fallback,
  str_snake_case,
  arr_map,
  OptionalValue,
  is_interval,
  str_replace,
  arr_reduce,
  str_contains,
  Identifiable
} from '../../../shared/utils/common';
import { DIALOG_DATA, DialogService } from '../../../services/dialog.service';
import { ConfigurableDropdownOption } from '../../../objects/studio';
import { CHECKLIST_TYPES, ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent } from '../../../module/checklists';
import { AssetTypeFaultSeverity } from '../../../module/asset-types';
import { Title } from '@angular/platform-browser';
import { LayoutDashboardService } from '../../../shared/layouts/layout-dashboard/layout-dashboard.service';
import { LooseObject } from '../../../objects/loose-object';
import { Relate } from '../../../objects/relate';
import { RecordService } from '../../../services/record.service';
import { Select } from '../../../objects/select';

@Component({
  selector: 'app-update-checklist',
  templateUrl: './update-checklist.component.html',
  styleUrls: [
    './update-checklist.component.scss',
  ],
})
export class UpdateChecklistComponent implements OnInit, OnDestroy {
  @Input() assetType?: ChecklistAssetType;

  @Input()
  set checklist(value: OptionalValue<Checklist>) {
    this._stored$.next(value);
    this._initializeData(value);
  }

  @Input() asInput: boolean = false;

  @Input() hideName: boolean = false;
  @Input() hideEnable: boolean = false;

  @Output('changed') $onChanged = new EventEmitter<UpdateChecklistComponentOnChangedEvent>();

  get displayName() : string {
    if (blank(this.name)) {
      return 'Unnamed';
    }

    if (this.type != 'asset') {
      return this.name;
    }

    if (this.name == data_get(this.assetType, 'name')
      || str_contains(this.name, { pattern: data_get(this.assetType, 'name')})
    ) {
      return this.name;
    }

    return spf('%s (%s)', {
      args: [this.name, fallback(data_get(this.assetType, 'name'), {
        fallback: () => 'No Asset Type',
      })]
    });
  }

  get headerTitle(): string {
    return `${this._translator.instant('checklist')}: ${this.displayName}`;
  }

  get specifics(): OptionalValue<string> {
    if (this.type != 'asset') {
      return null;
    }

    const standard = data_get(this.assetType, 'applicable_standards');

    if (blank(standard)) {
      return null;
    }

    return spf('Use %s standard', {
      args: [standard],
    });
  }

  id: string;

  type: ChecklistType = 'job';

  name: string;

  isEnabled: boolean = true;

  isCommentEnabled: boolean = false;

  requiredBefore?: ChecklistRequiredBefore = 'starting_every_travel';

  autoAttachedOn: string[] = [];

  perPromptGroupConfiguration?: Checklist['group_prompts_configuration'];

  documentOrdering?: number = 0;

  /// backward compatibility props
  availablePeriods: AvailablePeriodOption[] = [];

  errors: Record<string, string[]> = {};

  readonly prompts$ = new BehaviorSubject<ChecklistPrompt[]>([]);

  readonly fieldEntries$ = new BehaviorSubject<Record<string, PromptFieldEntry[]>>({
    assets: [],
    jobs: [],
    opportunities: [],
    asset_types: [],
    sites: [],
  });

  readonly fetching$ = new BehaviorSubject<boolean>(false);

  readonly deleting$ = new BehaviorSubject<boolean>(false);

  readonly saving$ = new BehaviorSubject<boolean>(false);

  readonly requierdBeforeOptions$ = new BehaviorSubject<FieldmagicDropdownOption<ConfigurableDropdownOption>[]>([]);

  readonly attachedOnOptions$ = new BehaviorSubject<FieldmagicDropdownOption<ConfigurableDropdownOption>[]>([]);

  readonly possibilities$ = new BehaviorSubject<AssetTypeSeverityFaultOption[]>([]);

  private readonly _stored$ = new BehaviorSubject<OptionalValue<Checklist>>(null);

  private _hovered: boolean = false;

  private _onFetchingChecklistSubscription?: Subscription;
  private _onPromptsChangesSubscription?: Subscription;

  constructor(
    private readonly _translator: TranslateService,
    private readonly _checklists: ChecklistsService,
    private readonly _pusher: PusherService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _notifications: NotificationService,
    private readonly _dialog: DialogService,
    private readonly _title: Title,
    private layoutDashboardService: LayoutDashboardService
  ) {}

  ngOnInit(): void {
    if (blank(this._stored$.value)) {
      this._onFetchingChecklistSubscription = observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._route.params.pipe(
          switchMap((params) => this._pusher.isRecordHasPresence('checklists', params.id).pipe(
            map((hasPresence) => ({
              hasPresence,
              id: params.id,
            })),
          )),
          tap(({ hasPresence }) => hasPresence && this._router.navigate(['checklists'], {
            replaceUrl: true
          })),
          filter(({ hasPresence }) => ! hasPresence),
          switchMap(({id}) => forkJoin({
            field_entries: this._checklists.getFieldEntries(),
            data: this._checklists.get(id),
            dropdown: this._checklists.getModulesDropdownOptions(),
          })),
        ),
      }).subscribe((results) => {
        this._initializeChecklist(results.data, results.field_entries, {
          dropdown: results.dropdown,
        });

        this._title.setTitle(spf('%s - Checklists | Fieldmagic', {
          args: [results.data.name],
        }));
      });
    } else {
      this._onFetchingChecklistSubscription = observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => forkJoin({
          data: of(this._stored$.value),
          dropdown: this._checklists.getModulesDropdownOptions(),
          field_entries: this._checklists.getFieldEntries(),
        })
      }).subscribe((results) => this._initializeChecklist(results.data, results.field_entries, {
        dropdown: results.dropdown,
      }));
    }
  }

  ngOnDestroy(): void {
    this._pusher.disconnectFromEditorsPresence();

    if (this._onFetchingChecklistSubscription) {
      this._onFetchingChecklistSubscription.unsubscribe();
    }

    if (this._onPromptsChangesSubscription) {
      this._onPromptsChangesSubscription.unsubscribe();
    }
  }

  onSave = () => observe({
    before: () => this.saving$.next(true),
    after: () => this.saving$.next(false),
    observable: () => defer(() => {
      if (! this._isValid()) {
        return of(false);
      }

      return this._checklists.update(this.id, {
        type: this.type,
        name: this.name,
        is_enabled: this.isEnabled,
        is_comment_enabled: this.isCommentEnabled,
        questions: {
          name: this.type == 'asset' ? 'per_asset_prompts' : 'main_prompts',
          prompts: this.prompts$.getValue(),
        },
        asset_type_id: data_get(this.assetType, 'id'),
        available_periods: this.availablePeriods,
        document_ordering: this.documentOrdering,
        required_before: this.requiredBefore,
        group_prompts_configuration: this.perPromptGroupConfiguration,
        auto_attached_on: this.autoAttachedOn,
      })
    }),
  }).pipe(
    filter((successful) => successful),
  ).subscribe((_) => {});

  onDelete = () => observe({
    before: () => this.deleting$.next(true),
    after: () => this.deleting$.next(false),
    observable: () => this._checklists.delete(this.id),
  }).subscribe((deleted) => {
    if (! deleted) {
      return;
    } else {
      this.layoutDashboardService.deleteRecentRecord(this.id);
    }

    this._router.navigate(['checklists']);
  });

  onAddPrompt(opts: {
    prompts?: ChecklistPrompt<AnswerableChecklistPrompt>[],
    group?: ChecklistPrompt<ChecklistPromptGroup>,
  } = {}): void {
    opts = Object.assign({
      prompts: this.prompts$.getValue(),
    }, opts);

    let destination = opts.prompts;

    if (filled(opts.group)) {
      destination = opts.group.prompts;
    }

    destination.push(this._makeInitialPrompt());

    this.prompts$.next(opts.prompts);
  }

  onGroupPromptAdd(): void {
    const prompts = this.prompts$.getValue();

    prompts.push(
      this._makeInitialGroupPrompt()
    );
  }

  onPromptRemove(pos: number, prompts: ChecklistPrompt[], opts: {
    group?: ChecklistPromptGroup,
  } = {}): void {
    let source = prompts;

    if (blank(prompts)) {
      return;
    }

    if (filled(opts.group)) {
      source = opts.group.prompts;
    }

    /// remove from source
    source.splice(pos, 1);

    /// update the prompts
    this.prompts$.next(prompts);
  }

  isDroppable = (): boolean => this._hovered;

  onHover = (value: boolean) => this._hovered = value;

  getRepeatingGroupsClassNames(prompts: ChecklistPrompt[]): string[] {
    let classNames: string[] = [];

    for (let index = 0; index < prompts.length; ++index) {
      if (! prompts[index].is_group) {
        continue;
      }

      classNames.push(
        spf('repeating-group-%d', {
          args: [index],
        }),
        spf('repeating-group-%d-header', {
          args: [index],
        }),
      );
    }

    return classNames;
  }

  getConnectedDroppableLists = (pos: number, source: ChecklistPrompt[]): string[] => ['outer'].concat(
    filter_array(
      this.getRepeatingGroupsClassNames(source),
      (className) => className != spf('repeating-group-%d', {
        args: [pos],
      })
    )
  );

  onPromptDragDropped(event: CdkDragDrop<string[]>, opts: {
    prompts: ChecklistPrompt<AnswerableChecklistPrompt | ChecklistPromptGroup>[],
    group?: ChecklistPromptGroup,
  }): void {
    const group = opts.group;
    const prompts = opts.prompts;
    const dropped: ChecklistPrompt = event.item.data;
    const container = prompts.find((prompt) => {
      if (! prompt.is_group) {
        return false;
      }

      const children = (prompt as ChecklistPromptGroup).prompts;

      if (blank(children)) {
        return false;
      }

      return children.findIndex((child) => child.id == dropped.id) !== -1;
    }) as ChecklistPromptGroup;

    /// dropping a prompt to a grouped prompt
    if (blank(container)
      && ! includes(data_get(group, 'prompts'), dropped)
      && ! dropped.is_group
      && ! isNil(group)
    ) {
      return this._dropPromptTo(prompts, {
        source: prompts,
        destination: group.prompts,
        fromIndex: event.previousIndex,
        toIndex: event.currentIndex,
      });
    }
    /// end

    /// dropping a prompt from a group to outside of the group (not to another group)
    const destination = prompts[event.currentIndex];

    if (filled(destination)
      && blank(group)
      && filled(container)
    ) {
      return this._dropPromptTo(prompts, {
        source: container.prompts,
        destination: prompts,
        fromIndex: event.previousIndex,
        toIndex: event.currentIndex,
      });
    }
    /// end

    /// dropping a prompt from a group to another group
    /// but shouldn't be on the same group as it source
    if (filled(container)
      && filled(group)
      && ! isEqual(container, group)
    ) {
      return this._dropPromptTo(prompts, {
        source: container.prompts,
        destination: group.prompts,
        fromIndex: event.previousIndex,
        toIndex: event.currentIndex,
      });
    }
    /// end

    /// dropping a prompt from a last group prompt outside
    if (! event.isPointerOverContainer
      && filled(dropped)
      && isEqual(container, last(prompts))
    ) {
      return this._dropPromptTo(prompts, {
        source: container.prompts,
        destination: prompts,
        fromIndex: event.previousIndex,
        toIndex: event.currentIndex,
      });
    }
    /// end

    /// dropping a prompt within the same group
    if (filled(group)) {
      return this._dropPromptTo(prompts, {
        source: group.prompts,
        destination: group.prompts,
        fromIndex: event.previousIndex,
        toIndex: event.currentIndex,
      });
    }
    /// end

    /// dropping a prompt within the same context
    /// a good example is re-positioning prompts one after another
    return this._dropPromptTo(prompts, {
      source: prompts,
      destination: prompts,
      fromIndex: event.previousIndex,
      toIndex: event.currentIndex,
    });
  }

  onOpenPerGroupPromptConfiguration = (prompt: ChecklistPrompt<ChecklistPromptGroup>) => this._dialog.show<
    ChecklistPromptGroupConfigDialogComponent, ChecklistPromptGroupConfigDialogProps, ChecklistPromptGroupConfigDialogResult
  >({
    component: ChecklistPromptGroupConfigDialogComponent,
    data: {
      configuration: fallback(data_get(this.perPromptGroupConfiguration, prompt.id), {
        fallback: () => ({
          prompt_per_column: 1,
          prompt_unit_price: 0,
        })
      }),
    },
    size: 'sm',
    afterClosing: (result) => {
      if (blank(result)) {
        return;
      }

      this.perPromptGroupConfiguration = {
        ... this.perPromptGroupConfiguration,
        ... {
          [prompt.id]: result.configuration,
        }
      };

      this._emitChange('group_prompts_configuration', this.perPromptGroupConfiguration);
    },
  });

  onRequiredBeforeChange(selected: FieldmagicDropdownOption<ConfigurableDropdownOption>): void {
    this.requiredBefore = data_get(selected, 'id');

    this._emitChange('required_before', this.requiredBefore);
  }

  onAttachedOnChange(selected: FieldmagicDropdownOption<ConfigurableDropdownOption>[]): void {
    this.autoAttachedOn = arr_map(selected, {
      transformer: (option) => data_get(option, 'id'),
    });

    this._emitChange('auto_attached_on', this.autoAttachedOn);
  }

  onNameChange = () => this._emitChange('name', this.name);

  onGroupChange(prompt: ChecklistPromptGroup, index: number): void {
    const current = this.prompts$.value;

    current[index] = prompt;

    this.prompts$.next(current);
  }

  onPromptsChange = () => this.prompts$.next([
    ... this.prompts$.value,
  ]);

  onIsEnabledChange = () => this._emitChange('is_checklist_enabled', this.isEnabled);

  onIsCommentEnabledChange = () => this._emitChange('is_comment_enabled', this.isCommentEnabled);

  onDocumentOrderChange = () => this._emitChange('document_ordering', this.documentOrdering);

  onGenerate(event: ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent): void {
    if (blank(event)) {
      return;
    }

    /// override prompts
    const family = event.questions.find((question) => (question.name == 'per_asset_prompts' && this.type == 'asset')
      || (this.type != 'asset' && question.name == 'main_prompts')
    );

    this.prompts$.next(family.prompts);
  }

  onAddPromptFault(prompt: ChecklistPrompt<AnswerableChecklistPrompt>): void {
    prompt.has_faults = true;
    prompt.faults = [];

    this.onPromptsChange();
  }

  onRemovePromptFault(prompt: ChecklistPrompt<AnswerableChecklistPrompt>): void {
    prompt.has_faults = false;
    prompt.faults = [];

    this.onPromptsChange();
  }

  onPromptFaultChange(selections: AssetTypeSeverityFaultOption[], prompt: ChecklistPrompt<AnswerableChecklistPrompt>): void {
    prompt.faults = arr_map(selections, {
      transformer: (fault) => data_get(fault, 'id'),
    });

    this.onPromptsChange();
  }

  onDuplicate = () => observe({
    before: () => this.saving$.next(true),
    after: () => this.saving$.next(false),
    observable: () => defer(() => {
      this._dialog.show({
        component: ConvertChecklistDialogComponent,
        data: {
          ...this,
          ...{
            action: 'duplicate'
          }
        },
        custom_settings: {
          width: '600px'
        },
        afterClosing: (result) => {
          if (blank(result)) {
            return;
          }
          this._checklists.duplicate(this.id, result).subscribe((response) => {
            this._stored$.next(null);
            this._notifications.notifySuccess('successfuly_converted');
            this._router.navigate([`checklists/edit`, response], {
              state: {
                fromListView: false
              },
              queryParams: {},
              replaceUrl: true,
            });
            this.ngOnInit();
          })
        },
      })
    }),
  }).subscribe((response) => {
    this._notifications.notifySuccess('checklist_duplicated_success');
    this._router.navigate([`checklists/edit/${response}`], { replaceUrl: true });
  });

  onConvert = () => observe({
    before: () => this.saving$.next(true),
    after: () => this.saving$.next(false),
    observable: () => defer(() => {
      this._dialog.show({
        component: ConvertChecklistDialogComponent,
        data: {
          ...this,
          ...{
            action: 'convert'
          }
        },
        custom_settings: {
          width: '600px'
        },
        afterClosing: (result) => {
          if (blank(result)) {
            return;
          }
          this._checklists.convert(this.id, result)
          .subscribe((response) => {
            this._stored$.next(null);
            this._notifications.notifySuccess('successfuly_converted');
            this._router.navigate([`checklists/edit`, response], {
              state: {
                fromListView: false
              },
              queryParams: {},
              replaceUrl: true,
            });
            this.ngOnInit();
          })
        },
      })
    }),
  }).subscribe((_) => {});

  tracker = (index: number, data: Identifiable) => data.id;

  private _emitChange<K extends keyof Checklist>(key: K, value: Checklist[K]): void {
    const checklist = {
      ... fallback<Checklist>(this._stored$.value, {
        fallback: () => ({
          id: generateId(),
          type: 'job',
          name: '',
          is_checklist_enabled: false,
          questions: [],
        }),
      }),
      ... ({
        [key]: value,
      }),
    };

    this._stored$.next(checklist);
    this.$onChanged.next(checklist);
  }

  private _initializeFieldEntries = (modules: Record<string, PromptFieldEntry[]>): void => this.fieldEntries$.next(modules);

  private _initializeData(data: Checklist): void {
    this.id = data.id;
    this.type = data.type;
    this.name = data.name;
    this.isEnabled = data.is_checklist_enabled;
    this.isCommentEnabled = data.is_comment_enabled;
    this.availablePeriods = data.available_periods;
    this.requiredBefore = data.required_before;
    this.documentOrdering = data.document_ordering;
    this.perPromptGroupConfiguration = fallback(data.group_prompts_configuration, {
      fallback: () => ({}),
    });

    this.autoAttachedOn = filled(data.auto_attached_on) ? data.auto_attached_on : [];

    if (data.type == 'asset'
      && filled(data_get(data, 'asset_type_id'))
    ) {
      this.assetType = {
        id: data.asset_type_id,
        name: data.asset_type_text,
        attributes: data.asset_type_attributes,
        applicable_standards: data.asset_type_applicable_standards,
      };
    }

    /// render prompts
    const family = data.questions.find((question) => (question.name == 'per_asset_prompts' && data.type == 'asset')
      || (data.type != 'asset' && question.name == 'main_prompts')
    );

    if (filled(family)) {
      const prompts = family.prompts;

      for (let i = 0; i < prompts.length; i++) {
        const prompt: ChecklistPrompt<AnswerableChecklistPrompt & ChecklistPromptGroup> = prompts[i];

        if (prompt.type == 'group'
          && filled(data_get(prompt, 'available_period'))
        ) {
          const period: string = data_get(prompt, 'available_period');

          if (is_interval(period)) {
            prompt.available_period = str_replace(period, {
              pattern: '_',
              with: () => ' ',
            });
          } else if (period == 'non_recurring') {
            prompt.available_period = '0 day';
          } else if (period == 'daily') {
            prompt.available_period = '1 day';
          } else if (period == 'weekly') {
            prompt.available_period = '1 week';
          } else if (period == 'monthly') {
            prompt.available_period = '1 month';
          } else if (period == 'quarterly') {
            prompt.available_period = '3 month';
          } else if (period == 'bi-annual') {
            prompt.available_period = '6 month';
          } else if (period == 'annual') {
            prompt.available_period = '1 year';
          } else if (period.startsWith('every_') && period.endsWith('_years')) {
            prompt.available_period = spf('%d year', {
              args: [toNumber(str_replace(period, {
                pattern: /[^0-9]+/g,
                with: () => '',
              }))],
            })
          }
        }

        prompts[i] = prompt;
      }

      this.prompts$.next(prompts);
    } else {
      this.prompts$.next([]);
    }

    const raw: AssetTypeFaultSeverity[] = fallback(data_get(data, 'asset_type_severities'), {
      fallback: () => [],
    });

    const possibilities = arr_reduce(raw, {
      initial_value: [],
      reducer: (initial, severity) => {
        initial.push(... arr_map(severity.faults, {
          transformer: (fault) => ({
            id: fault.id,
            text: fault.fault,
            fault: fault.fault,
            fault_severity_id: severity.id,
            color: severity.color,
            groupName: severity.severity,
            severity: severity.severity,
          }),
        }));

        return initial;
      },
    });

    this.possibilities$.next(possibilities);
    this.layoutDashboardService.updateRecentRecords(data, 'checklists');
  }

  private _initializeChecklist(data: Checklist, fieldEntries: Record<string, PromptFieldEntry[]>, opts: {
    dropdown?: ModulesDropdownOptions,
  } = {}): void {
    /// initialize data
    this._initializeData(data);

    /// initialize field entries
    this._initializeFieldEntries(fieldEntries);

    const requiredBeforeOptions: FieldmagicDropdownOption<ConfigurableDropdownOption>[] = fallback(data_get(opts, 'dropdown.checklists.required_before'), {
      fallback: () => [],
    });

    this.requierdBeforeOptions$.next(requiredBeforeOptions.filter((option) => {
      if (this.type == 'opportunity'
          && (option.id.includes('_travel') || option.id.includes('_work'))
      ) {
        return false;
      }

      return true;
    }));

    /// initialize dropdown options
    const attachedOnOptions: FieldmagicDropdownOption<ConfigurableDropdownOption>[] = [];

    if (this.type != 'opportunity') {
      attachedOnOptions.push({
        id: 'on_jobs_create',
        text: this._translator.instant('Every new job'),
        groupName: 'job',
      });

      if (filled(data_get(opts, 'dropdown.jobs.type'))) {
        const types: ConfigurableDropdownOption[] = data_get(opts, 'dropdown.jobs.type');

        for (const type of types) {
          attachedOnOptions.push({
            id: spf('on_jobs_create_with_type_%s', {
              args: [str_snake_case(type.id, {
                lowercase: true,
              })],
            }),
            text: this._translator.instant('on_job_create_with_type', {
              job_type: this._translator.instant(type.text),
            }),
            groupName: 'job',
          });
        }
      }
    } else {
      attachedOnOptions.push({
        id: 'on_opportunities_create',
        text: this._translator.instant('on_quote_create'),
        groupName: 'quote',
      });
    }

    this.attachedOnOptions$.next(attachedOnOptions);

    this._stored$.next({
      is_checklist_enabled: fallback(this.isEnabled, {
        fallback: () => this.isEnabled,
      }),
      is_comment_enabled: fallback(this.isCommentEnabled, {
        fallback: () => false,
      }),
      document_ordering: fallback(this.documentOrdering, {
        fallback: () => 0,
      }),
      auto_attached_on: this.autoAttachedOn,
      ... data,
    });

    this._onPromptsChangesSubscription = this.prompts$.subscribe((prompts) => this._emitChange('questions', [{
      name: this.type == 'asset' ? 'per_asset_prompts' : 'main_prompts',
      prompts: prompts,
    }]));
  }

  /**
   * Make an initial prompt that will be used during creation of prompts
   *
   * FC-3781: set default prompt as pass/fail
   */
  private _makeInitialPrompt = (): ChecklistPrompt<AnswerableChecklistPrompt> => ({
    id: generateId(),
    prompt: '',
    type: 'pass/fail',
    value : {
      pass: [this._translator.instant('pass')],
      fail: [this._translator.instant('fail')],
      pending: [this._translator.instant('Not Applicable')],
      is_passed: 'default',
    },
    schedule_type: {
      always: true,
    },
    is_required: true,
    is_visible_in_reports: true,
    is_image_enabled: false,
    is_group: false,
  });

  private _makeInitialGroupPrompt = (): ChecklistPrompt<ChecklistPromptGroup> => ({
    id: generateId(),
    group_name: '',
    type: 'group',
    prompts: [
      this._makeInitialPrompt(),
    ],
    is_group: true,
    available_period: 'always',
  });

  private _dropPromptTo(prompts: ChecklistPrompt[], opts: {
    source: ChecklistPrompt[],
    destination: ChecklistPrompt[],
    fromIndex: number,
    toIndex: number,
  }): void {
    transferArrayItem(opts.source, opts.destination, opts.fromIndex, opts.toIndex);

    /// attempt rebuild?
    this.prompts$.next(prompts);
  }

  private _isValid(): boolean {
    this.errors = {};

    if (blank(this.name)) {
      this.errors['name'] = ['field_is_required'];
    }

    if (this.documentOrdering > 999 || this.documentOrdering < -999) {
      this.errors['document_ordering'] = ['This field must not exceed 999 and must not be less than -999.'];
    }

    const prompts = this.prompts$.getValue();

    for (const prompt of prompts) {
      /// validates sub/child prompts
      if (prompt.type == 'group' && prompt.is_group) {
        for (const subPrompt of data_get(prompt, 'prompts', { default_value: [] })) {
          this._validatePrompt(subPrompt);
        }
      }

      this._validatePrompt(prompt);
    }

    const isValid = blank(this.errors);

    if (! isValid) {
      this._notifications.notifyError('Please make sure that the checklist information are correct.');
    }

    return isValid;
  }

  private _validatePrompt(prompt: ChecklistPrompt): void {
    if (blank(data_get(prompt, 'prompt')) && blank(data_get(prompt, 'group_name'))) {
      this.errors[spf('%s:prompt', {
        args: [prompt.id],
      })] = ['The prompt must not be empty'];
    }

    if (prompt.type == 'group' && blank(data_get(prompt, 'prompts'))) {
      this.errors[spf('%s:prompt', {
        args: [prompt.id]
      })] = ['Missing child prompts'];
    }

    if (blank(data_get(prompt, 'type'))) {
      this.errors[spf('%s:type', {
        args: [prompt.id],
      })] = ['A type must be specified.'];
    }

    const valueErrorKey = spf('%s:value', {
      args: [prompt.id]
    });

    if (prompt.type == 'pass/fail' && blank(data_get(prompt, 'value.pass'))) {
      this.errors[spf('%s:pass', {args: [valueErrorKey]})] = ['A pass option must be provided.'];
    }

    if (prompt.type == 'pass/fail' && blank(data_get(prompt, 'value.fail'))) {
      this.errors[spf('%s:fail', {args: [valueErrorKey]})] = ['A fail option must be provided.'];
    }

    if (prompt.type == 'instructions' && blank(data_get(prompt, 'value.instructions_text'))) {
      this.errors[valueErrorKey] = ['field_is_required'];
    }

    if (prompt.type == 'field_entry' && blank(data_get(prompt, 'value.field_entry'))) {
      this.errors[valueErrorKey] = ['field_is_required'];
    }

    if (prompt.type == 'dropdown' && blank(data_get(prompt, 'value.dropdown'))) {
      this.errors[valueErrorKey] = ['field_is_required'];
    }
  }
}

@Component({
  selector: 'checklist-group-prompt-configuration',
  template: `
    <fieldmagic-dialog
      icon="cog"
      label="Group Configuration"
      [instance]="self"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          purpose="save"
          (click)="onSave()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-number-input
          label="{{ 'cells_per_row' | translate }}"
          [(ngModel)]="promptPerColumn"
          [errors]="errors | data_get: 'prompt_per_column'"
          tooltip="Allows the prompts under this group to be displayed as column."
        >
        </fieldmagic-number-input>
        <fc-currency-input
          label="{{ 'annual_price_per_asset' | translate }}"
          [places]="4"
          [(ngModel)]="promptUnitPrice"
          [nonNegative]="true"
          [isGroup]="true"
        >
        </fc-currency-input>
      </ng-template>

    </fieldmagic-dialog>
  `,
})
export class ChecklistPromptGroupConfigDialogComponent {
  promptPerColumn: number = fallback(this._props.configuration.prompt_per_column, {
    fallback: () => 1,
  });

  promptUnitPrice: number = fallback(this._props.configuration.prompt_unit_price, {
    fallback: () => 0,
  });

  errors: Record<string, string[]> = {};

  get self(): ChecklistPromptGroupConfigDialogComponent {
    return this
  }

  constructor(
   @Inject(DIALOG_DATA) private readonly _props: ChecklistPromptGroupConfigDialogProps,
   private readonly _dialog: DialogService,
  ) {}

  onSave(): void {
    if (! this._isValid()) {
      return;
    }

    this._dialog.close<this, ChecklistPromptGroupConfigDialogResult>({
      instance: this,
      result: {
        configuration: {
          ... this._props.configuration,
          prompt_per_column: this.promptPerColumn,
          prompt_unit_price: this.promptUnitPrice,
        }
      }
    });
  }

  private _isValid(): boolean {
    const errors: Record<string, string[]> = {};

    if (this.promptPerColumn < 1) {
      errors['prompt_per_column'] = ['Must be atleast 1 column.'];
    } else if (this.promptPerColumn > 4) {
      errors['prompt_per_column'] = ['Must not be greater than 4 columns.'];
    }

    this.errors = errors;

    return blank(errors);
  }
}

type ChecklistPromptGroupConfigDialogProps = {
  configuration: ChecklistPromptGroupConfiguration;

}

type ChecklistPromptGroupConfigDialogResult = {
  configuration: ChecklistPromptGroupConfiguration;
}

export type UpdateChecklistComponentOnChangedEvent = Checklist;

type AssetTypeSeverityFaultOption = FieldmagicDropdownOption<{
  id: string;
  fault_severity_id: string;
  fault: string;
  color: string;
  severity: string;
}>


@Component({
  selector: 'app-convert-checklist',
  template: `
    <fieldmagic-dialog
      icon="plus-circle"
      label="add_checklist"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [label]="isConvert ? 'convert' : 'duplicate'"
          (click)="onSave()"
          [icon]="isConvert ? 'arrow-alt-circle-right' : 'copy'"
        >
        </fieldmagic-primary-button>
        <fieldmagic-primary-button
          variant="error"
          (click)="onCancel()"
          icon="times"
          label="close"
        ></fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
          <div fieldmagicGridColumn="12">
            <fieldmagic-text-input
              [(ngModel)]="checklistName"
              [withRequiredMarker]="true"
              [label]="'name'"
              [standalone]="false"
            >
            </fieldmagic-text-input>
          </div>

          <ng-container *ngIf="isConvert">
            <div fieldmagicGridColumn="12">
              <fieldmagic-dropdown-input
                [(ngModel)]="checklistType"
                [label]="'checklist_type'"
                [withRequiredMarker]="true"
                [options]="checklistTypeList"
              >
              </fieldmagic-dropdown-input>
            </div>

            <div *ngIf="isAsset" fieldmagicGridColumn="12">
              <span class="fieldmagic-text-input-label fieldmagic-text-with-required-mark">{{ 'asset_type' | translate }}</span>
              <ng-select
                [(ngModel)]="objAssetRelate.value"
                (open)="objAssetRelate.loadDataOnClick()"
                [items]="objAssetRelate.source | async"
                [loading]="objAssetRelate.loader"
                [typeahead]="objAssetRelate.typehead"
                bindLabel="text"
                [ngModelOptions]="{standalone: true}"
                appendTo="body"
                ngDefaultControl
              ></ng-select>
            </div>
          </ng-container>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class ConvertChecklistDialogComponent {

  checklistName: string = '';

  checklistType: LooseObject = {};

  currentChecklistType: string = '';

  checklistTypeList = CHECKLIST_TYPES.map((option) => (
    new Select(option, `${option}_checklist`)
  ));

  objAssetRelate = new Relate<LooseObject[]>();

  get isAsset(): boolean {
    return get(this.checklistType, 'id') == 'asset';
  }

  get isConvert(): boolean {
    return get(this._props, 'action') == 'convert';
  }

  constructor(
   private readonly _dialog: DialogService,
   private readonly _recordService: RecordService,
   private readonly _notification: NotificationService,
   @Inject(DIALOG_DATA) private readonly _props: LooseObject,
  ) {
    this.checklistName = this._props.name;
    this.currentChecklistType = this._props.type;

    let arAssetType = [];
    if (filled(get(this._props, 'assetType'))) {
      arAssetType = [
        new Select(
          this._props.assetType.id,
          this._props.assetType.name
        )
      ]
    }

    this.objAssetRelate.buildRelates(
      switchMap(strTerm => this._recordService.getRecordRelate('asset_types', strTerm, '', false)),
      arAssetType,
      filled(arAssetType)
    );

    let intCurrentTypeIndex = this.checklistTypeList.findIndex(
      data => data.id ==  this.currentChecklistType
    );
    if (intCurrentTypeIndex > -1) {
      this.checklistTypeList.splice(intCurrentTypeIndex, 1);
    }
  }

  onSave(): void {
    if (this.isConvert) {
      if (this.isAsset && blank(get(this.objAssetRelate, 'value.id'))) {
        this._notification.notifyWarning('asset_type_required');
        return;
      }

      if (filled(this.checklistName) && filled(this.checklistType)) {
        let strChecklistType = get(this.checklistType, 'id');
        this._dialog.close<this, any>({
          instance: this,
          result: {
            name: this.checklistName,
            checklist_type: strChecklistType,
            asset_type_id: strChecklistType == 'asset' ? get(this.objAssetRelate, 'value.id') : null,
          }
        });
      }
    } else {
      if (filled(this.checklistName)) {
        this._dialog.close<this, any>({
          instance: this,
          result: {
            name: this.checklistName,
          }
        });
      }
    }
  }

  onCancel(): void {
    this._dialog.close<this, any>({
      instance: this,
      result: {}
    });
  }
}